import type { UseQueryResult } from '@tanstack/react-query'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Skeleton } from '@/components/feedback/Skeleton'
import { EmailFrame } from '@/components/ui/EmailFrame'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Select } from '@/components/ui/Select'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { useFormDraft } from '@/components/ui/useFormDraft'
import type { StatusSendPayload } from '@/features/cases/api/useCase'
import {
  statusRefusalMessage,
  useCaseStatusPreview,
} from '@/features/cases/api/useStatusEmail'
import { CAP, can } from '@/lib/can'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import type { CaseDetail, CurrentUser, StatusEmailPreview } from '@/types/domain'

/* Telling the client where their shipment is (FR-41).
 *
 * A sheet rather than a one-click send, because this is the rare action in the
 * app that leaves the system permanently: an email cannot be unsent, and the
 * address it goes to is a fact worth reading before committing rather than
 * after. The confirmation is the recipient line, not a second "are you sure" —
 * a dialog that only asks for a second click teaches people to click twice.
 *
 * The status itself is NOT editable here. This sheet reports the case's current
 * status; changing it is the transition endpoint's job, and a control that
 * silently did both would let "tell the client" become "close the case".
 */

/** The languages a client is written to in. The team's own interface language
 *  is a separate setting — the person sending and the person reading rarely
 *  share one, which is why this defaults to Russian rather than to the UI. */
const LANGUAGES = ['ru', 'en'] as const

/** How long the note must rest before the preview re-renders it (S-15). The
 *  language and, on the consolidated sheet, the selection reach the preview at
 *  once; a note is typed a key at a time, and each key would otherwise be a
 *  server render of the whole email. */
export const NOTE_SETTLE_MS = 1000

/** «Предпросмотр»: the exact email, under the fields that change it (S-15).
 *
 *  Shared by both send sheets, which differ only in which question they ask
 *  the server. What it shows, in order of precedence:
 *
 *    · `idle` — a sentence instead of a request, where the sheet has nothing to
 *      ask about yet (the consolidated sheet with nothing ticked);
 *    · the REFUSAL, in place of the frame — the preview is refused exactly as
 *      the send would be, so a branding gap or a missing address is read here
 *      before anybody presses Send. Through `statusRefusalMessage`, the problem
 *      → i18n path, never the server's English `detail`;
 *    · a skeleton until the first email arrives;
 *    · the subject and the email in `EmailFrame`. While a new language or note
 *      renders, the previous email stays in the frame (`keepPreviousData`) and
 *      the region says it is busy, rather than flashing a skeleton per change.
 *
 *  Not rendered at all where the send itself is refused up front (no contact
 *  access, no address): the sheet's notice already says why, and a preview of
 *  an email nobody can send answers a question nobody asked. */
export function StatusPreviewPanel({
  preview,
  idle,
}: {
  preview: UseQueryResult<StatusEmailPreview>
  idle?: string
}) {
  const { t } = useTranslation()
  const headingId = useId()

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        {/* Regular weight: a heading, and headings are never bold. */}
        <h3 id={headingId} className="text-text-heading text-[13px] leading-tight font-normal">
          {t('cases.statusSend.preview.title')}
        </h3>
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('cases.statusSend.preview.hint')}
        </p>
      </div>

      {idle !== undefined ? (
        <p className="text-text-muted text-[13px] leading-snug">{idle}</p>
      ) : preview.isError ? (
        <Banner title={statusRefusalMessage(preview.error, t)} />
      ) : preview.data === undefined ? (
        <div aria-busy="true" aria-label={t('cases.statusSend.preview.loading')}>
          <Skeleton className="h-[26rem] rounded-[var(--radius-DEFAULT)]" />
        </div>
      ) : (
        <div className="flex flex-col gap-2" aria-busy={preview.isFetching || undefined}>
          <p className="text-[12.5px] leading-snug">
            <span className="text-text-muted font-light">
              {t('cases.statusSend.preview.subject')}:
            </span>{' '}
            <span className="text-text-heading">{preview.data.subject}</span>
          </p>
          <EmailFrame
            html={preview.data.html}
            title={t('cases.statusSend.preview.frameTitle')}
            height="26rem"
          />
        </div>
      )}
    </section>
  )
}

/** The per-case preview's question, mounted inside the sheet's body so it
 *  lives exactly as long as the open sheet: the settled note starts from the
 *  draft each time it opens, never from what was typed into the last one. */
function CaseStatusPreview({
  caseId,
  note,
  language,
}: {
  caseId: string
  note: string
  language: 'en' | 'ru'
}) {
  const settledNote = useDebouncedValue(note, NOTE_SETTLE_MS)
  const preview = useCaseStatusPreview(caseId, { note: settledNote, language }, true)
  return <StatusPreviewPanel preview={preview} />
}

export function CaseStatusSendSheet({
  open,
  onClose,
  record,
  user,
  onSubmit,
  pending,
  submitError,
}: {
  open: boolean
  onClose: () => void
  record: CaseDetail
  /** The sender, for the one capability this sheet asks about: whether they
   *  may read the client's contacts (`directory.contact.read`). */
  user: CurrentUser | undefined
  onSubmit: (values: StatusSendPayload) => void
  pending?: boolean
  /** What the SERVER refused, if it did. */
  submitError?: string
}) {
  const { t } = useTranslation()

  const [draft, setDraft] = useFormDraft<StatusSendPayload>(
    open ? record.id : null,
    () => ({ note: '', language: 'ru' }),
  )

  /* A client with no address on file cannot be told anything. This is a
     standing limitation rather than a failure — nothing went wrong, the
     directory is simply incomplete — so it renders as a neutral notice and
     blocks the send, instead of a red error over a form that could never
     have worked. */
  const recipient = record.clientContactEmail?.trim()

  /* ABSENT for a second reason, and it must not be worded as the first (story
     CI, R9 — the quote send's Q4 applied to this send). A role without
     `directory.contact.read` is served no `clientContactEmail`, and the server
     refuses its send with a 403: the send would hand it the address as
     `sentTo`. Telling that reader "the client has no email" would send them to
     add one that already exists. The capability is asked first, so it decides
     even if an address did arrive — the server refuses that send regardless —
     and the address is then not shown either. */
  const noContactAccess = !can(user, CAP.directoryContactRead)
  const missingContact = noContactAccess || !recipient
  const blockedReason = noContactAccess
    ? t('cases.statusSend.noContactAccess')
    : !recipient
      ? t('cases.statusSend.noContact')
      : undefined

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('cases.statusSend.title')}
      submitLabel={t('cases.statusSend.submit')}
      onSubmit={() => onSubmit(draft)}
      pending={pending}
      /* A refusal the reader can act on (ask a colleague, fix the directory):
         `blockedReason`, so the button keeps its tab stop and its tooltip, and
         the same sentence as the notice so a phone, which has no hover, reads
         it too. `pending` stays the only real `disabled`. */
      blockedReason={blockedReason}
      error={submitError}
      notice={blockedReason}
    >
      <div className="flex flex-col gap-4">
        {/* The two facts the sender is committing to, stated before the fields
            they can actually change: who receives this, and what it will say.
            Read-only on purpose — the recipient comes from the client record so
            a send cannot be redirected from here, and the status is reported
            rather than set. */}
        <div className="border-line bg-surface-sunken flex flex-col gap-3 rounded-lg border px-4 py-3">
          <div className="flex flex-col gap-1.5">
            <span className="asa-eyebrow">{t('cases.statusSend.recipient')}</span>
            {missingContact ? (
              <span className="text-text-muted text-[13px]">—</span>
            ) : (
              <span className="text-text-heading text-[13px] leading-snug">
                {record.clientContactName ? (
                  <>
                    {record.clientContactName}{' '}
                    <span className="text-text-muted">&lt;{recipient}&gt;</span>
                  </>
                ) : (
                  recipient
                )}
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="asa-eyebrow">{t('cases.statusSend.status')}</span>
            <span className="flex">
              <CaseStatusPill status={record.status} />
            </span>
          </div>
        </div>

        <Field
          label={t('cases.statusSend.language')}
          hint={t('cases.statusSend.languageHint')}
        >
          {(props) => (
            <Select
              {...props}
              value={draft.language}
              onChange={(language) => setDraft((d) => ({ ...d, language }))}
              /* The full names, not the "EN"/"RU" chips the topbar switcher
                 uses: this picks the language a CLIENT is written in, and a
                 two-letter code beside a client's address invites the glance
                 that mistakes it for the sender's own interface setting. */
              options={LANGUAGES.map((value) => ({
                value,
                label: t(value === 'ru' ? 'common.language.russian' : 'common.language.english'),
              }))}
              disabled={missingContact}
            />
          )}
        </Field>

        <Field label={t('cases.statusSend.note')} hint={t('cases.statusSend.noteHint')}>
          {(props) => (
            <Textarea
              {...props}
              value={draft.note}
              onChange={(event) =>
                setDraft((d) => ({ ...d, note: event.target.value }))
              }
              placeholder={t('cases.statusSend.notePlaceholder')}
              disabled={missingContact}
              rows={4}
            />
          )}
        </Field>

        {/* The email exactly as this send would mail it, below the two fields
            that change it. Asked for only when the send is possible: the
            server refuses the preview to exactly the senders it refuses the
            send to, and the notice above already tells them why. The note is
            sent as the send would send it — the draft, untrimmed. */}
        {missingContact ? null : (
          <CaseStatusPreview
            caseId={record.id}
            note={draft.note}
            language={draft.language}
          />
        )}
      </div>
    </FormSheet>
  )
}
